import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {Box, Chip, Divider, Stack, Typography} from '@mui/material';
import {useReportStore} from '../../useReportStore';
import {getIcrByNumber as getIcrByNumberTest} from '../../../collection-requests/_test_data/ICRequests';
import {
    getIcrByNumber as getIcrByNumberFS,
    resolveIcrNumber,
    resolveReportLinkedIcrNumber,
} from '../../../../firebase/firestore/icr';
import {
    resolveIcrPriority,
    resolvePirEeis,
    resolvePirQuestion,
    resolvePirTimeframe,
    resolvePirTitle,
} from '../../../collection-requests/pirPayload';
import {
    formatIcrClassification,
    formatIcrDueDateDisplay,
    isPastDueDate,
} from '../../../collection-requests/icrHeaderFormat';
import {icrPriorityTextColor} from '../../../collection-requests/components/IcrPriorityLabel';
import Timestamp from '../../../../components/Timestamp';

/** Inline badge for classification / status chips in the title area. */
const StatusBadge = ({children, color = 'text.secondary', bg = '#EBEDF0'}) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: bg,
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: 0.4,
            lineHeight: 1.4,
            color,
            textTransform: 'uppercase',
            userSelect: 'none',
            flexShrink: 0,
        }}
    >
        {children}
    </Box>
);

/** Labeled section block used for Question / Assessment / Area of Interest. */
const SectionBlock = ({label, children}) => (
    <Stack spacing={0.5}>
        <Typography
            variant="overline"
            sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: 0.8,
                color: 'text.disabled',
                lineHeight: 1.6,
            }}
        >
            {label}
        </Typography>
        {children}
    </Stack>
);

const BodyText = ({children}) => (
    <Typography variant="body2" sx={{lineHeight: 1.7, color: children ? 'text.primary' : 'text.disabled'}}>
        {children || '—'}
    </Typography>
);

const TagChips = ({items = []}) => (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
        {items.map((t) => <Chip key={t} label={t} size="small" variant="outlined" sx={{fontSize: '0.75rem'}}/>)}
    </Stack>
);

const IcrSection = () => {
    const navigate = useNavigate();
    const icrNumber = useReportStore((state) => resolveReportLinkedIcrNumber({
        id: state.reportId,
        meta: state.meta,
    }));
    const decoded = icrNumber ? decodeURIComponent(icrNumber) : undefined;
    const [icr, setIcr] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!decoded) {
                setIcr(null);
                return;
            }
            setLoading(true);
            try {
                const fsResult = await getIcrByNumberFS(decoded);
                if (!cancelled) {
                    setIcr(fsResult || getIcrByNumberTest(decoded));
                }
            } catch (e) {
                if (!cancelled) setIcr(getIcrByNumberTest(decoded));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [decoded]);

    if (loading) {
        return (
            <Stack p={2.5}>
                <Typography variant="body2" color="text.secondary">Loading ICR…</Typography>
            </Stack>
        );
    }

    if (!icr) {
        return (
            <Stack p={2.5} spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>No requirement linked</Typography>
                <Typography variant="body2" color="text.secondary">
                    No information collection requirement found for this report.
                </Typography>
            </Stack>
        );
    }

    const title = resolvePirTitle(icr);
    const question = resolvePirQuestion(icr);
    const tf = resolvePirTimeframe(icr);
    const eeis = resolvePirEeis(icr);
    const displayPriority = resolveIcrPriority(icr);
    const owner = icr.owner || icr.createdBy || null;
    const createdAt = icr.createdAt || null;

    const classificationLabel = formatIcrClassification(icr.classification);
    const due = formatIcrDueDateDisplay(tf.due);
    const priorityColor = icrPriorityTextColor(displayPriority);

    return (
        <Stack height="100%" overflow="auto" bgcolor="background.paper">

            {/* ── Header block ── */}
            <Stack px={2.5} pt={2.5} pb={2} spacing={1.5}>

                {/* Badge row: classification · priority · workflow status */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    {classificationLabel && classificationLabel !== '—' && (
                        <StatusBadge color="text.secondary">{classificationLabel}</StatusBadge>
                    )}
                    {displayPriority && (
                        <StatusBadge color={priorityColor}>{displayPriority} Priority</StatusBadge>
                    )}
                    {icr.workflowStatus && icr.workflowStatus !== 'Active' && (
                        <Chip
                            label={icr.workflowStatus}
                            color={icr.workflowStatus === 'Pending Approval' ? 'warning' : 'default'}
                            size="small"
                        />
                    )}
                </Stack>

                {/* Title */}
                <Typography variant="h6" fontWeight={700} sx={{lineHeight: 1.3}}>
                    {title || 'Untitled ICR'}
                </Typography>

                {/* Identifier line: ICR# · DTG · UUID */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    {icrNumber && (
                        <Typography variant="caption" fontFamily="monospace" color="text.secondary" component="span">
                            {icrNumber}
                        </Typography>
                    )}
                    {icr.dtg && (
                        <>
                            <Typography variant="caption" color="text.disabled" component="span">•</Typography>
                            <Typography variant="caption" fontFamily="monospace" color="text.secondary" component="span">
                                {icr.dtg}
                            </Typography>
                        </>
                    )}
                    {icr.id && icr.id !== icrNumber && (
                        <>
                            <Typography variant="caption" color="text.disabled" component="span">•</Typography>
                            <Typography
                                variant="caption"
                                fontFamily="monospace"
                                color="text.disabled"
                                component="span"
                                sx={{fontSize: '0.65rem'}}
                            >
                                {icr.id}
                            </Typography>
                        </>
                    )}
                </Stack>

                {/* Secondary meta: owner · created date */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    {owner && (
                        <Typography variant="caption" color="text.secondary" component="span">
                            {owner}
                        </Typography>
                    )}
                    {createdAt && (
                        <>
                            {owner && (
                                <Typography variant="caption" color="text.disabled" component="span">•</Typography>
                            )}
                            <Timestamp
                                component={Typography}
                                variant="caption"
                                color="text.secondary"
                                value={createdAt}
                                fallback="—"
                            />
                        </>
                    )}
                    {/* Due date — always visible, highlighted if past */}
                    {(tf.due || due.label !== 'Due date: —') && (
                        <>
                            {(owner || createdAt) && (
                                <Typography variant="caption" color="text.disabled" component="span">•</Typography>
                            )}
                            <Typography
                                variant="caption"
                                component="span"
                                sx={{
                                    color: isPastDueDate(tf.due) ? 'error.main' : 'text.secondary',
                                    fontWeight: isPastDueDate(tf.due) ? 600 : 400,
                                }}
                            >
                                {due.label}
                            </Typography>
                        </>
                    )}
                </Stack>
            </Stack>

            <Divider/>

            {/* ── Body sections ── */}
            <Stack px={2.5} py={2} spacing={2.5}>

                <SectionBlock label="Question">
                    <BodyText>{question}</BodyText>
                </SectionBlock>

                {icr.currentAssessment && (
                    <>
                        <Divider sx={{borderStyle: 'dashed'}}/>
                        <SectionBlock label="Current Assessment">
                            <BodyText>{icr.currentAssessment}</BodyText>
                        </SectionBlock>
                    </>
                )}

                {icr.areaOfInterest && (
                    <>
                        <Divider sx={{borderStyle: 'dashed'}}/>
                        <SectionBlock label="Area of Interest">
                            <BodyText>{icr.areaOfInterest}</BodyText>
                        </SectionBlock>
                    </>
                )}

                {eeis.length > 0 && (
                    <>
                        <Divider sx={{borderStyle: 'dashed'}}/>
                        <SectionBlock label={`Essential Elements of Information (${eeis.length})`}>
                            <Stack spacing={1.5} pt={0.25}>
                                {eeis.map((eei, idx) => {
                                    const key = eei.id || eei.eeiNumber || eei.text;
                                    const label = eei.eeiNumber || eei.id;
                                    const canNavigate = Boolean(
                                        eei.eeiNumber || (eei.id && !String(eei.id).startsWith('legacy-')),
                                    );
                                    return (
                                        <Stack key={key} direction="row" spacing={1.25} alignItems="flex-start">
                                            {/* Index badge */}
                                            <Box
                                                sx={{
                                                    minWidth: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    bgcolor: 'action.selected',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mt: 0.25,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{fontSize: '0.625rem', fontWeight: 700, lineHeight: 1, color: 'text.secondary'}}
                                                >
                                                    {idx + 1}
                                                </Typography>
                                            </Box>
                                            <Stack spacing={0.25} minWidth={0}>
                                                {label && (
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        fontFamily="monospace"
                                                        color={canNavigate ? 'primary.main' : 'text.disabled'}
                                                        sx={{
                                                            cursor: canNavigate ? 'pointer' : 'default',
                                                            textDecoration: canNavigate ? 'underline' : 'none',
                                                            width: 'fit-content',
                                                        }}
                                                        onClick={() => {
                                                            if (!canNavigate) return;
                                                            navigate(
                                                                `/icr/eei/${encodeURIComponent(eei.eeiNumber || eei.id)}`,
                                                            );
                                                        }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                )}
                                                <BodyText>{eei.text}</BodyText>
                                            </Stack>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </SectionBlock>
                    </>
                )}

                {(icr.tags || []).length > 0 && (
                    <>
                        <Divider sx={{borderStyle: 'dashed'}}/>
                        <SectionBlock label="Tags">
                            <TagChips items={icr.tags}/>
                        </SectionBlock>
                    </>
                )}
            </Stack>
        </Stack>
    );
};

export default IcrSection;
